/* Modern article template — behaviour
 *
 * Deferred, dependency-free, and idempotent: every modern article section that
 * needs JS requests this same file, so it may be referenced more than once on a
 * page. The init guard below makes the second and later loads no-ops.
 *
 * Handles:
 *   - reading progress bar        [data-mb-progress]
 *   - share button                [data-mb-share]
 *   - FAQ accordions              [data-mb-faq-question]
 */
(function () {
  'use strict';

  if (window.__mbArticleInit) return;
  window.__mbArticleInit = true;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- progress */
  function initProgress() {
    var bar = document.querySelector('[data-mb-progress]');
    if (!bar) return;

    var target =
      document.querySelector('[data-mb-progress-target]') ||
      document.getElementById('mb-article-body') ||
      document.querySelector('.mb-article');
    if (!target) return;

    var frame = null;

    function update() {
      frame = null;
      var top = target.getBoundingClientRect().top + window.scrollY;
      var height = target.offsetHeight - window.innerHeight;
      var amount = height > 0 ? ((window.scrollY - top) / height) * 100 : 0;
      bar.style.width = Math.max(0, Math.min(100, amount)) + '%';
    }

    function schedule() {
      if (frame === null) frame = window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  /* ------------------------------------------------------------------ share */
  function initShare() {
    var buttons = document.querySelectorAll('[data-mb-share]');
    if (!buttons.length) return;

    Array.prototype.forEach.call(buttons, function (button) {
      var status = document.querySelector('[data-mb-share-status]');

      button.addEventListener('click', function () {
        function say(message) {
          if (status) status.textContent = message;
        }

        say(button.getAttribute('data-mb-share-preparing') || 'Preparing share link…');

        var shareData = {
          title: document.title,
          text: button.getAttribute('data-mb-share-text') || document.title,
          url: window.location.href
        };

        if (navigator.share) {
          navigator
            .share(shareData)
            .then(function () {
              say(button.getAttribute('data-mb-share-shared') || 'Share sheet opened.');
            })
            .catch(function (error) {
              if (error && error.name !== 'AbortError') {
                say(button.getAttribute('data-mb-share-fallback') || 'Copy the page URL to share this guide.');
              } else {
                say('');
              }
            });
          return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(window.location.href)
            .then(function () {
              say(button.getAttribute('data-mb-share-copied') || 'Article link copied.');
            })
            .catch(function () {
              say(button.getAttribute('data-mb-share-fallback') || 'Copy the page URL to share this guide.');
            });
          return;
        }

        say(button.getAttribute('data-mb-share-fallback') || 'Copy the page URL to share this guide.');
      });
    });
  }

  /* -------------------------------------------------------------------- FAQ */
  function initFaq() {
    var questions = document.querySelectorAll('[data-mb-faq-question]');
    if (!questions.length) return;

    Array.prototype.forEach.call(questions, function (button) {
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        var answer = document.getElementById(button.getAttribute('aria-controls'));
        var list = button.closest('[data-mb-faq-list]');

        // Optional single-open behaviour, set per section.
        if (!expanded && list && list.getAttribute('data-mb-faq-single') === 'true') {
          Array.prototype.forEach.call(list.querySelectorAll('[data-mb-faq-question]'), function (other) {
            if (other === button) return;
            other.setAttribute('aria-expanded', 'false');
            var otherAnswer = document.getElementById(other.getAttribute('aria-controls'));
            if (otherAnswer) otherAnswer.hidden = true;
          });
        }

        button.setAttribute('aria-expanded', String(!expanded));
        if (answer) answer.hidden = expanded;
      });
    });
  }

  /* ------------------------------------------------- smooth in-page anchors */
  function initAnchors() {
    if (prefersReducedMotion) return;
    var links = document.querySelectorAll('[data-mb-anchor]');
    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', function (event) {
        var id = link.getAttribute('href');
        if (!id || id.charAt(0) !== '#' || id.length < 2) return;
        var target = document.getElementById(id.slice(1));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Keep the URL and focus behaviour a plain anchor would have given.
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        if (history.replaceState) history.replaceState(null, '', id);
      });
    });
  }

  /* --------------------------------------------------- rich-text heading ids */
  /* The rail's table of contents is generated server-side with the same rule:
     an author-supplied id wins, otherwise mb-h2-<ordinal>. Keep both in step. */
  function initHeadingIds() {
    var rte = document.querySelector('[data-mb-rte]');
    if (!rte) return;
    Array.prototype.forEach.call(rte.querySelectorAll('h2'), function (heading, index) {
      if (!heading.id) heading.id = 'mb-h2-' + (index + 1);
    });
  }

  /* ------------------------------------------------- theme sticky-header offset */
  /* The rail sticks below whatever the theme pins to the top of the viewport.
     Measured rather than hardcoded, because this theme's header is sticky at
     some widths and not others. */
  function initHeaderOffset() {
    var root = document.querySelector('.mb-article');
    if (!root) return;

    function measure() {
      var offset = 0;
      var nodes = document.querySelectorAll('header, [class*="header"], [id*="header"], .top-bar');
      Array.prototype.forEach.call(nodes, function (el) {
        if (el.closest && el.closest('.mb-root')) return;
        var cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        var r = el.getBoundingClientRect();
        // A header bar: pinned near the top, a plausible height, and wide.
        if (r.height < 30 || r.height > 220) return;
        if (r.top > 120) return;
        if (r.width < window.innerWidth * 0.6) return;
        offset = Math.max(offset, r.bottom);
      });
      document.querySelectorAll('.mb-root').forEach(function (el) {
        el.style.setProperty('--mb-header-offset', Math.round(Math.max(0, offset)) + 'px');
      });
    }

    measure();
    window.addEventListener('resize', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  function init() {
    initHeadingIds();
    initHeaderOffset();
    initProgress();
    initShare();
    initFaq();
    initAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('.mb-root')) {
      window.__mbArticleInit = false;
      init();
      window.__mbArticleInit = true;
    }
  });
})();
